import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSearch, FiChevronDown, FiChevronUp, FiHelpCircle } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { apiUrls, getHttpHeaders } from '../config/api';

// TypeScript Interfaces
interface FaqPageProps {
  onHomeClick: () => void;
  onBooksClick: () => void;
  onVideoLearningClick: () => void;
  onBlogsClick: () => void;
  onLoginClick: () => void;
  onAdminClick: () => void;
  onMkAcademyOnlineClick?: () => void;
  onMkAcademyInPersonClick?: () => void;
  onFaqClick?: () => void;
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  user?: any;
  onLogout?: () => void;
}

interface Faq {
  id: number;
  question: string;
  answer_html: string;
  category: string;
  tags: string[];
  display_order: number;
  published_at: string;
}

interface GroupedFaqs {
  [category: string]: Faq[];
}

// CSS Variables
const CSS_VARS = {
  primaryColor: '#253C8C',
  secondaryColor: '#F9D51D',
  textLight: '#718096',
  bgLight: '#f8f9ff',
  shadowLight: '0 4px 20px rgba(37, 60, 140, 0.1)',
  shadowHover: '0 8px 30px rgba(37, 60, 140, 0.2)',
};

const FaqSection = styled.section`
  padding-top: 120px;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
`;

const FaqContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
  padding-top: 10px;
`;

const FaqHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const FaqTitle = styled.h1`
  font-size: 2.5rem;
  color: ${CSS_VARS.primaryColor};
  margin-bottom: 10px;
  font-weight: 700;
`;

const FaqSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${CSS_VARS.textLight};
  margin-bottom: 30px;
`;

const SearchContainer = styled.div`
  position: relative;
  max-width: 500px;
  margin: 0 auto 40px auto;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 15px 50px 15px 20px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  outline: none;
  transition: all 0.3s ease;

  &:focus {
    border-color: ${CSS_VARS.primaryColor};
    box-shadow: 0 0 0 3px rgba(37, 60, 140, 0.1);
  }

  &::placeholder {
    color: ${CSS_VARS.textLight};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: ${CSS_VARS.textLight};
`;

const CategorySection = styled.div`
  margin-bottom: 30px;
`;

const CategoryTitle = styled.h2`
  font-size: 1.5rem;
  color: ${CSS_VARS.primaryColor};
  margin-bottom: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FaqItem = styled.div`
  background: white;
  border-radius: 12px;
  margin-bottom: 15px;
  box-shadow: ${CSS_VARS.shadowLight};
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${CSS_VARS.shadowHover};
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  padding: 20px;
  background: none;
  border: none;
  text-align: left;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${CSS_VARS.primaryColor};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;

  &:hover {
    background: ${CSS_VARS.bgLight};
  }

  &:focus {
    outline: 2px solid ${CSS_VARS.primaryColor};
    outline-offset: -2px;
  }
`;

const FaqAnswer = styled.div<{ isOpen: boolean }>`
  max-height: ${props => props.isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  border-top: ${props => props.isOpen ? '1px solid #e5e7eb' : 'none'};
`;

const FaqAnswerContent = styled.div`
  padding: 20px;
  color: #374151;
  line-height: 1.6;

  p {
    margin-bottom: 15px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  ul, ol {
    margin: 15px 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 8px;
  }

  strong {
    color: ${CSS_VARS.primaryColor};
  }

  a {
    color: ${CSS_VARS.primaryColor};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${CSS_VARS.textLight};
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.5;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #dc2626;
`;

const FaqPage: React.FC<FaqPageProps> = ({ 
  onHomeClick, 
  onBooksClick, 
  onVideoLearningClick, 
  onBlogsClick, 
  onLoginClick, 
  onAdminClick,
  onMkAcademyOnlineClick,
  onMkAcademyInPersonClick,
  onFaqClick,
  isAuthenticated,
  isAdmin,
  user,
  onLogout
}) => {
  const [faqs, setFaqs] = useState<GroupedFaqs>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());
  const [filteredFaqs, setFilteredFaqs] = useState<GroupedFaqs>({});

  useEffect(() => {
    fetchFaqs();
  }, []);

  useEffect(() => {
    filterFaqs();
  }, [faqs, searchTerm]);

  // Generate JSON-LD structured data for SEO
  useEffect(() => {
    if (Object.keys(faqs).length > 0) {
      const faqItems = Object.values(faqs).flat().slice(0, 10); // Limit to top 10 for performance
      
      const jsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqItems.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer_html
          }
        }))
      };

      // Add JSON-LD to document head
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(jsonLd);
      script.id = 'faq-jsonld';
      
      // Remove existing JSON-LD if present
      const existing = document.getElementById('faq-jsonld');
      if (existing) {
        existing.remove();
      }
      
      document.head.appendChild(script);

      // Cleanup function
      return () => {
        const scriptToRemove = document.getElementById('faq-jsonld');
        if (scriptToRemove) {
          scriptToRemove.remove();
        }
      };
    }
  }, [faqs]);

  const fetchFaqs = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('🔍 Fetching public FAQs...');
      
      const response = await fetch(apiUrls.faqs.list, {
        headers: getHttpHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Public FAQs data:', data);
      
      if (data.success && data.faqs) {
        setFaqs(data.faqs);
      } else {
        console.warn('Unexpected FAQ data format:', data);
        setFaqs({});
      }
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError('Failed to load FAQs. Please try again later.');
      setFaqs({});
    } finally {
      setLoading(false);
    }
  };

  const filterFaqs = (): void => {
    if (!searchTerm.trim()) {
      setFilteredFaqs(faqs);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered: GroupedFaqs = {};

    Object.entries(faqs).forEach(([category, categoryFaqs]) => {
      const matchingFaqs = categoryFaqs.filter(faq => 
        faq.question.toLowerCase().includes(searchLower) ||
        faq.answer_html.toLowerCase().includes(searchLower) ||
        faq.category.toLowerCase().includes(searchLower)
      );

      if (matchingFaqs.length > 0) {
        filtered[category] = matchingFaqs;
      }
    });

    setFilteredFaqs(filtered);
  };

  const toggleFaq = (faqId: number): void => {
    setOpenItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(faqId)) {
        newSet.delete(faqId);
      } else {
        newSet.add(faqId);
      }
      return newSet;
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  };

  const renderFaqContent = (html: string): string => {
    // Basic HTML sanitization for display
    return html;
  };

  if (loading) {
    return (
      <FaqSection>
        <Header 
          onHomeClick={onHomeClick} 
          onBooksClick={onBooksClick} 
          onVideoLearningClick={onVideoLearningClick} 
          onBlogsClick={onBlogsClick} 
          onLoginClick={onLoginClick} 
          onAdminClick={onAdminClick}
          onMkAcademyOnlineClick={onMkAcademyOnlineClick}
          onMkAcademyInPersonClick={onMkAcademyInPersonClick}
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
          user={user}
          onLogout={onLogout}
        />
        <FaqContainer>
          <LoadingContainer>
            <div style={{ fontSize: '24px', color: CSS_VARS.primaryColor }}>Loading FAQs...</div>
          </LoadingContainer>
        </FaqContainer>
      </FaqSection>
    );
  }

  if (error) {
    return (
      <FaqSection>
        <Header 
          onHomeClick={onHomeClick} 
          onBooksClick={onBooksClick} 
          onVideoLearningClick={onVideoLearningClick} 
          onBlogsClick={onBlogsClick} 
          onLoginClick={onLoginClick} 
          onAdminClick={onAdminClick}
          onMkAcademyOnlineClick={onMkAcademyOnlineClick}
          onMkAcademyInPersonClick={onMkAcademyInPersonClick}
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
          user={user}
          onLogout={onLogout}
        />
        <FaqContainer>
          <ErrorContainer>
            <div style={{ fontSize: '24px', marginBottom: '20px' }}>{error}</div>
            <button onClick={fetchFaqs} style={{ 
              padding: '10px 20px', 
              backgroundColor: CSS_VARS.primaryColor, 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px',
              cursor: 'pointer'
            }}>
              Retry
            </button>
          </ErrorContainer>
        </FaqContainer>
      </FaqSection>
    );
  }

  return (
    <>
      <FaqSection>
        <Header 
          onHomeClick={onHomeClick} 
          onBooksClick={onBooksClick} 
          onVideoLearningClick={onVideoLearningClick} 
          onBlogsClick={onBlogsClick} 
          onLoginClick={onLoginClick} 
          onAdminClick={onAdminClick}
          onMkAcademyOnlineClick={onMkAcademyOnlineClick}
          onMkAcademyInPersonClick={onMkAcademyInPersonClick}
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
          user={user}
          onLogout={onLogout}
          currentPage="faq"
        />
        <FaqContainer>
          <FaqHeader>
            <FaqTitle>Frequently Asked Questions</FaqTitle>
            <FaqSubtitle>
              Find answers to common questions about MathKnots platform and services
            </FaqSubtitle>
          </FaqHeader>

          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={handleSearchChange}
              aria-label="Search FAQs"
            />
            <SearchIcon>
              <FiSearch size={20} />
            </SearchIcon>
          </SearchContainer>

          {Object.keys(filteredFaqs).length > 0 ? (
            Object.entries(filteredFaqs)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([category, categoryFaqs]) => (
                <CategorySection key={category}>
                  <CategoryTitle>
                    <FiHelpCircle />
                    {category}
                  </CategoryTitle>
                  {categoryFaqs
                    .sort((a, b) => a.display_order - b.display_order)
                    .map((faq) => (
                      <FaqItem key={faq.id}>
                        <FaqQuestion
                          onClick={() => toggleFaq(faq.id)}
                          aria-expanded={openItems.has(faq.id)}
                          aria-controls={`faq-answer-${faq.id}`}
                        >
                          {faq.question}
                          {openItems.has(faq.id) ? 
                            <FiChevronUp size={20} /> : 
                            <FiChevronDown size={20} />
                          }
                        </FaqQuestion>
                        <FaqAnswer 
                          isOpen={openItems.has(faq.id)}
                          id={`faq-answer-${faq.id}`}
                          role="region"
                          aria-labelledby={`faq-question-${faq.id}`}
                        >
                          <FaqAnswerContent 
                            dangerouslySetInnerHTML={{ 
                              __html: renderFaqContent(faq.answer_html) 
                            }} 
                          />
                        </FaqAnswer>
                      </FaqItem>
                    ))}
                </CategorySection>
              ))
          ) : (
            <EmptyState>
              <EmptyIcon>
                <FiHelpCircle />
              </EmptyIcon>
              <h3>No FAQs found</h3>
              {searchTerm ? (
                <p>No FAQs match your search term "{searchTerm}". Try a different search.</p>
              ) : (
                <p>No FAQs are currently available. Please check back later.</p>
              )}
            </EmptyState>
          )}
        </FaqContainer>
      </FaqSection>
      <Footer 
        onBooksClick={onBooksClick}
        onVideoLearningClick={onVideoLearningClick}
        onBlogsClick={onBlogsClick}
        onMkAcademyOnlineClick={onMkAcademyOnlineClick}
        onFaqClick={onFaqClick}
      />
    </>
  );
};

export default FaqPage;
